import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Reorder, motion, useDragControls } from "framer-motion";
import { ZoomIn, ZoomOut, Maximize2, GripVertical, Mic, Clapperboard, Minus, Plus, Check, Music, Palette, ScrollText, RefreshCw, ChevronLeft, ChevronRight, Wand2 } from "lucide-react";
import type { StoryboardClip, StoryboardProject } from "./types";
import { requestClipApprove, requestClipDuration, requestClipReorder, requestClipSelect, requestClipRegen } from "./studioBus";
import RenderGate, { useBoardRender } from "./RenderGate";
import { useNarrowViewport } from "./MobileReviewSheet";
import { useAuthMediaUrl } from "@/lib/authMedia";
import MediaZoomViewer from "./MediaZoomViewer";
import TrackScroller, { SETTLE_MS } from "./TrackScroller";
import ReasonChips, { useReasonPicks } from "./ReasonChips";

// StoryboardTimeline — the always-visible, CapCut-style clip timeline for the
// Director studio. Zoom, drag-to-reorder, hover-scrub, and per-clip length
// controls. Edits are dispatched over the studio bus; the chat-driven flow
// (CorridoChatFlow, on the local ComfyUI pipeline) owns the actual state and
// republishes, so the timeline is always a live mirror of the real project.
//
// Renders bound to SAMPLE_PROJECT when no live project exists, so the region
// is never blank — users see what the workspace does before their first run.

export const SAMPLE_PROJECT: StoryboardProject = {
  runId: "",
  service: "corrido",
  selectedIdx: 0,
  totalDurationSec: 21,
  clips: [
    // `theme` holds the locale KEY for the sample board only; a live board
    // carries real engine text, which is data and stays as sent.
    { id: "sample:0", idx: 0, kind: "artist", imageUrl: "/promo/poster_traditional.jpg", theme: "studio.timeline.sample.0", durationSec: 4, status: "draft" },
    { id: "sample:1", idx: 1, kind: "broll", imageUrl: "/promo/still_tumbado.jpg", theme: "studio.timeline.sample.1", durationSec: 3, status: "draft" },
    { id: "sample:2", idx: 2, kind: "broll", imageUrl: "/promo/still_sadsierreno.jpg", theme: "studio.timeline.sample.2", durationSec: 3, status: "draft" },
    { id: "sample:3", idx: 3, kind: "artist", imageUrl: "/promo/still_romantico.jpg", theme: "studio.timeline.sample.3", durationSec: 4, status: "draft" },
    { id: "sample:4", idx: 4, kind: "broll", imageUrl: "/promo/poster_belico.jpg", theme: "studio.timeline.sample.4", durationSec: 3, status: "draft" },
    { id: "sample:5", idx: 5, kind: "artist", imageUrl: "/promo/poster_traditional.jpg", theme: "studio.timeline.sample.5", durationSec: 4, status: "draft" },
  ],
};

// The SAMPLE board stores locale keys in `theme`; a live board carries real
// engine text. Anything that is not a known key renders as-is.
const clipTheme = (t: TFunction, clip: StoryboardClip) =>
  clip.theme && clip.theme.startsWith("studio.timeline.sample.") ? t(clip.theme) : clip.theme;

const MIN_CLIP_SEC = 1;
/** Timeline zoom range. The top used to be 2×, which still left faces too small
 *  to judge (owner report 2026-10-05); 4× fills a board node with one scene,
 *  and the magnifier on each card opens the deep-zoom viewer beyond that. */
export const TIMELINE_ZOOM_MIN = 0.6;
export const TIMELINE_ZOOM_MAX = 4;
const TIMELINE_ZOOM_STEP = 0.25;
const MAX_CLIP_SEC = 8;

function formatSec(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Clip still, fetched with the auth token (the image route is auth-gated and
 *  a bare <img src> can't send a Bearer header — it 401'd every thumbnail). */
function ClipImage({ clip, position }: { clip: StoryboardClip; position: number }) {
  const { t } = useTranslation();
  const url = useAuthMediaUrl(clip.imageUrl);
  if (!url) return <div className={`h-full w-full bg-white/5 ${clip.status === "rendering" ? "animate-pulse" : ""}`} aria-hidden />;
  return (
    <img
      src={url}
      alt={clipTheme(t, clip) || t("studio.timeline.clipAlt", { n: position + 1 })}
      loading="lazy" className={`h-full w-full object-cover ${clip.status === "regenerating" ? "opacity-40" : ""}`}
      draggable={false}
    />
  );
}

function ClipCard({
  clip,
  position,
  zoom,
  selected,
  isSample,
  trimEnabled,
  onSelect,
  onDuration,
  onApprove,
  onZoom,
}: {
  clip: StoryboardClip;
  position: number;
  zoom: number;
  selected: boolean;
  isSample: boolean;
  /** False for the corrido board: the engine cuts every scene to the song's
   *  beat and ignores any length the page sends, so no trim is offered. */
  trimEnabled: boolean;
  onSelect: (idx: number) => void;
  onDuration: (idx: number, durationSec: number) => void;
  onApprove: (idx: number) => void;
  /** Open this clip in the full-screen deep-zoom viewer. */
  onZoom: (idx: number) => void;
}) {
  const { t } = useTranslation();
  // The picture is something to hold the board by (a drag on it moves the
  // board) and to click (it selects the scene). A scene changes place from its
  // own handle, bottom-left, or with Earlier / Later in the inspector.
  const reorder = useDragControls();
  const height = Math.round(96 * zoom);
  // CapCut-style: horizontal footprint tracks clip length, so longer clips
  // read wider on the timeline — only where a length can actually be set.
  const width = Math.round(height * (9 / 16) * (trimEnabled ? clip.durationSec / 3 : 1));
  const approved = clip.status === "approved";

  // CapCut-style trim: drag the clip's RIGHT EDGE to resize its length.
  // Pointer capture keeps the drag on the handle; stopPropagation keeps
  // framer-motion's reorder drag from hijacking it. 0.5s snap, clamped.
  const resizeRef = useRef<{ startX: number; startSec: number } | null>(null);
  const pxPerSec = (height * (9 / 16)) / 3;
  const onResizeDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeRef.current = { startX: event.clientX, startSec: clip.durationSec };
  };
  const onResizeMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeRef.current) return;
    const raw = resizeRef.current.startSec + (event.clientX - resizeRef.current.startX) / Math.max(1, pxPerSec);
    const snapped = Math.round(raw * 2) / 2;
    const clamped = Math.min(MAX_CLIP_SEC, Math.max(MIN_CLIP_SEC, snapped));
    if (clamped !== clip.durationSec) onDuration(clip.idx, clamped);
  };
  const onResizeUp = (event: React.PointerEvent<HTMLDivElement>) => {
    resizeRef.current = null;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* already released */ }
  };

  return (
    <Reorder.Item
      value={clip}
      as="div" className={[
        "group relative flex-shrink-0 cursor-grab overflow-hidden rounded-lg border-2 bg-black/40 active:cursor-grabbing",
        approved
          ? "border-emerald-500 shadow-[0_0_14px_rgba(16,185,129,0.35)]" : selected ? "border-primary shadow-[0_0_18px_hsl(var(--primary)/0.35)]" : "border-border",
      ].join(" ")}
      style={{ width, height }}
      dragListener={false}
      dragControls={reorder}
      onClick={() => onSelect(clip.idx)}
      onDoubleClick={() => onZoom(clip.idx)}
      whileDrag={{ scale: 1.05, zIndex: 30 }}
      layout
      data-testid={`studio-clip-${clip.idx}`}
    >
      <ClipImage clip={clip} position={position} />
      <span className="absolute left-1 top-1 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-bold text-white">
        {position + 1}
      </span>
      <span className="absolute right-1 top-1 rounded bg-black/75 p-0.5 text-white/70">
        {clip.kind === "broll" ? <Clapperboard className="h-3 w-3" /> : <Mic className="h-3 w-3" />}
      </span>
      {/* Approval toggle — green check when approved, outline when pending. */}
      <button
        type="button" aria-label={approved ? t("studio.timeline.unapproveClip") : t("studio.timeline.approveClip")}
        title={approved ? t("studio.timeline.approvedClickToUnapprove") : t("studio.timeline.approveForRender")}
        onClick={(event) => { event.stopPropagation(); onApprove(clip.idx); }}
        data-testid={`studio-clip-approve-${clip.idx}`}
        className={[
          "absolute right-1 top-6 rounded-full p-0.5 transition",
          approved ? "bg-emerald-500 text-black" : "bg-black/70 text-white/60 opacity-0 group-hover:opacity-100",
        ].join(" ")}
      >
        <Check className="h-3 w-3" />
      </button>
      {/* Magnifier — the deep-zoom viewer. Always shown on touch screens (no
          hover there); pointerdown is kept from starting a reorder drag. */}
      <button
        type="button" aria-label={t("studio.timeline.zoomClip", { n: position + 1 })}
        title={t("studio.timeline.zoomClip", { n: position + 1 })}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => { event.stopPropagation(); onZoom(clip.idx); }}
        data-testid={`studio-clip-zoom-${clip.idx}`}
        className="absolute left-1 top-6 rounded-full bg-black/70 p-1 text-white/85 transition hover:bg-black [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
      >
        <Maximize2 className="h-3.5 w-3.5" />
      </button>
      {trimEnabled && (
        <span className="absolute bottom-1 right-2.5 rounded bg-black/75 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          {clip.durationSec.toFixed(1)}s
        </span>
      )}
      {/* The reorder handle: a mouse shows it on hover. A screen with no hover
          (a phone) has none: an invisible handle would catch the taps meant for
          the picture (a finger's tap snaps to the nearest target); there a
          scene moves with Earlier / Later. A click on it selects the scene. */}
      <span
        data-board-keep data-testid={`studio-clip-reorder-${clip.idx}`}
        title={t("studio.canvas.reorderClip")} aria-label={t("studio.canvas.reorderClip")}
        onPointerDown={(event) => { event.stopPropagation(); reorder.start(event); }}
        className="absolute bottom-1 left-1 cursor-grab touch-none select-none rounded bg-black/70 p-1 text-white/80 opacity-0 transition group-hover:opacity-100 active:cursor-grabbing [@media(hover:none)]:hidden"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </span>
      {/* A redraw in flight: the old still stays visible (dimmed) under a plain
          label until the new one lands or the redraw fails. */}
      {clip.status === "regenerating" && (
        <span
          data-testid={`studio-clip-redrawing-${clip.idx}`}
          className="absolute inset-0 flex items-center justify-center bg-black/55 text-center text-[10px] font-bold text-white"
        >
          {t("studio.timeline.regenerating")}
        </span>
      )}
      {/* Right-edge trim handle (CapCut-style resize) — only where the engine
          honours a length. */}
      {trimEnabled && (
        <div
          role="slider" aria-label={t("studio.timeline.clipLength", { n: position + 1 })}
          aria-valuemin={MIN_CLIP_SEC}
          aria-valuemax={MAX_CLIP_SEC}
          aria-valuenow={clip.durationSec}
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={onResizeUp}
          onClick={(event) => event.stopPropagation()}
          data-testid={`studio-clip-resize-${clip.idx}`}
          className="absolute inset-y-0 right-0 w-2 cursor-ew-resize bg-gradient-to-l from-white/25 to-transparent opacity-0 transition group-hover:opacity-100" />
      )}
      {selected && trimEnabled && (
        <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-center gap-2 opacity-0 transition group-hover:opacity-100">
          <button
            type="button" aria-label={t("studio.timeline.shorterClip")} disabled={isSample || clip.durationSec <= MIN_CLIP_SEC}
            onClick={(event) => { event.stopPropagation(); onDuration(clip.idx, Math.max(MIN_CLIP_SEC, clip.durationSec - 0.5)); }}
            className="rounded-full bg-black/80 p-1.5 text-white hover:bg-black disabled:opacity-40" >
            <Minus className="h-3.5 w-3.5" />
          </button>
          <button
            type="button" aria-label={t("studio.timeline.longerClip")} disabled={isSample || clip.durationSec >= MAX_CLIP_SEC}
            onClick={(event) => { event.stopPropagation(); onDuration(clip.idx, Math.min(MAX_CLIP_SEC, clip.durationSec + 0.5)); }}
            className="rounded-full bg-black/80 p-1.5 text-white hover:bg-black disabled:opacity-40" >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </Reorder.Item>
  );
}

export default function StoryboardTimeline({ project }: { project?: StoryboardProject | null }) {
  const { t } = useTranslation();
  const live = project ?? null;
  const isSample = !live;
  const board = live ?? SAMPLE_PROJECT;

  const [zoom, setZoomRaw] = useState(1); // TIMELINE_ZOOM_MIN … TIMELINE_ZOOM_MAX
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);
  const [scrubSec, setScrubSec] = useState<number | null>(null);
  const [steer, setSteer] = useState(""); // per-clip direction note for regen
  // "What's wrong?" chips for the selected still: cleared on another pick and
  // once the redraw is sent. They only teach; the redraw itself is unchanged.
  const [reasons, setReasons, clearReasons] = useReasonPicks(isSample ? null : board.selectedIdx);
  const trackRef = useRef<HTMLDivElement | null>(null);
  // Zooming keeps the scene in the middle of the strip in the middle, instead
  // of the strip jumping back toward the first card.
  const zoomAnchor = useRef<number | null>(null);
  const setZoom = (next: number) => {
    const z = Math.min(TIMELINE_ZOOM_MAX, Math.max(TIMELINE_ZOOM_MIN, Math.round(next * 100) / 100));
    const track = trackRef.current;
    if (track && track.scrollWidth > 0) zoomAnchor.current = (track.scrollLeft + track.clientWidth / 2) / track.scrollWidth;
    setZoomRaw(z);
  };
  useLayoutEffect(() => {
    const track = trackRef.current;
    const frac = zoomAnchor.current;
    zoomAnchor.current = null;
    if (!track || frac === null) return;
    // The cards glide to their new size over a few frames; hold the anchor
    // in the middle of the strip the whole way.
    const hold = () => { track.scrollLeft = Math.max(0, frac * track.scrollWidth - track.clientWidth / 2); };
    hold();
    const until = performance.now() + SETTLE_MS;
    let frame = requestAnimationFrame(function tick(now) {
      hold();
      if (now < until) frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [zoom]);
  const boardRender = useBoardRender();
  const narrow = useNarrowViewport();
  // Sample mode keeps its own order/selection so the demo is fully interactive.
  const [sampleClips, setSampleClips] = useState<StoryboardClip[]>(SAMPLE_PROJECT.clips);
  const [sampleSelected, setSampleSelected] = useState<number | null>(0);
  const [sampleDurations, setSampleDurations] = useState<Record<number, number>>({});
  const [sampleApproved, setSampleApproved] = useState<Set<number>>(new Set());

  const clips = isSample
    ? sampleClips.map((clip) => ({
        ...clip,
        durationSec: sampleDurations[clip.idx] ?? clip.durationSec,
        status: sampleApproved.has(clip.idx) ? ("approved" as const) : clip.status,
      }))
    : board.clips;
  const approvedCount = clips.filter((clip) => clip.status === "approved" || clip.status === "rendering" || clip.status === "rendered").length;
  const selectedIdx = isSample ? sampleSelected : board.selectedIdx;
  const totalSec = useMemo(() => clips.reduce((sum, clip) => sum + clip.durationSec, 0), [clips]);

  // Live editing surface: regenerate / reorder / approve-all / render all happen
  // on THIS board now (moved off the chat, user directive 2026-07-21). Only a
  // live run in the stills "board" phase is editable; a job in flight is read-only.
  const isLiveBoard = !isSample && board.phase === "board";
  const anyRegenerating = clips.some((clip) => clip.status === "regenerating");
  // The corrido engine cuts every scene to the song's beat; a length set here
  // was sent and never read (audit 2026-09-23, should-fix 9). No handle is
  // shown for it. The prop stays for a service whose engine honours a length.
  const trimEnabled = board.service !== "corrido";
  const selectedClip = clips.find((clip) => clip.idx === selectedIdx) ?? null;
  const selectedPos = selectedClip ? clips.findIndex((c) => c.idx === selectedClip.idx) : -1;
  const allApproved = clips.length > 0 && clips.every((clip) => clip.status === "approved" || clip.status === "rendering" || clip.status === "rendered");
  const renderCost = board.renderCostCredits ?? null;
  const creditBalance = board.creditBalance ?? null;
  const approveAll = () => clips.forEach((clip) => { if (clip.status !== "approved") requestClipApprove(clip.idx); });
  const moveSelected = (dir: -1 | 1) => {
    if (selectedPos < 0) return;
    const target = selectedPos + dir;
    if (target < 0 || target >= clips.length) return;
    const order = clips.map((c) => c.idx);
    [order[selectedPos], order[target]] = [order[target], order[selectedPos]];
    requestClipReorder(order);
  };

  // Which clip sits under the scrub playhead?
  const scrubClipIdx = useMemo(() => {
    if (scrubSec === null) return null;
    let acc = 0;
    for (const clip of clips) {
      acc += clip.durationSec;
      if (scrubSec < acc) return clip.idx;
    }
    return clips[clips.length - 1]?.idx ?? null;
  }, [clips, scrubSec]);

  const handleReorder = (next: StoryboardClip[]) => {
    if (isSample) { setSampleClips(next); return; }
    requestClipReorder(next.map((clip) => clip.idx));
  };
  const handleSelect = (idx: number) => {
    if (isSample) { setSampleSelected(idx); return; }
    requestClipSelect(idx);
  };
  const handleDuration = (idx: number, durationSec: number) => {
    if (isSample) { setSampleDurations((prev) => ({ ...prev, [idx]: durationSec })); return; }
    requestClipDuration(idx, durationSec);
  };
  const handleApprove = (idx: number) => {
    if (isSample) {
      setSampleApproved((prev) => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx); else next.add(idx);
        return next;
      });
      return;
    }
    requestClipApprove(idx);
  };

  const handleScrub = (event: React.MouseEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left + track.scrollLeft) / Math.max(1, track.scrollWidth)));
    setScrubSec(ratio * totalSec);
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-border bg-card/80 p-4 shadow-[0_18px_40px_rgba(0,0,0,0.3)]" data-testid="studio-storyboard" >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t("studio.timeline.header", { clips: clips.length, length: formatSec(totalSec), approved: approvedCount })}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {isSample
              ? t("studio.timeline.sampleNote")
              : board.phase === "working"
                ? t("studio.timeline.buildingNote")
                : t("studio.timeline.liveNote", { service: board.service })}
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="studio-timeline-zoom">
          <button
            type="button" aria-label={t("studio.timeline.zoomOutTimeline")} disabled={zoom <= TIMELINE_ZOOM_MIN}
            onClick={() => setZoom(zoom - TIMELINE_ZOOM_STEP)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-black/40 text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            <ZoomOut className="h-5 w-5" />
          </button>
          <input
            type="range" min={TIMELINE_ZOOM_MIN}
            max={TIMELINE_ZOOM_MAX}
            step={0.01}
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            aria-label={t("studio.timeline.zoomTimeline")} data-testid="studio-timeline-zoom-slider" className="studio-range w-40 sm:w-56" />
          <button
            type="button" aria-label={t("studio.timeline.zoomInTimeline")} disabled={zoom >= TIMELINE_ZOOM_MAX}
            onClick={() => setZoom(zoom + TIMELINE_ZOOM_STEP)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-black/40 text-muted-foreground hover:text-foreground disabled:opacity-40"
          >
            <ZoomIn className="h-5 w-5" />
          </button>
          <span className="w-11 text-right text-[11px] tabular-nums text-muted-foreground">{Math.round(zoom * 100)}%</span>
        </div>
      </div>

      {/* RENDER CONTRACT header — song, photos (with verdicts), style, beats,
          cost. Everything the Director is about to render, in one strip. */}
      {live && (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="studio-board-contract">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-black/30 px-2.5 py-1 text-[11px] text-foreground">
            <Music className="h-3 w-3 text-primary" />
            {live.songName || t("studio.timeline.noSong")}
          </span>
          {live.styleLabel && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-black/30 px-2.5 py-1 text-[11px] text-foreground">
              <Palette className="h-3 w-3 text-primary" />
              {live.styleLabel}
            </span>
          )}
          {(live.photos?.length ?? 0) > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-black/30 px-2 py-0.5">
              {live.photos!.slice(0, 5).map((photo) => (
                <span key={photo.name} className="relative inline-block" title={`${photo.name}${photo.reason ? ` — ${photo.reason}` : ""}`}>
                  {photo.url
                    ? <img src={photo.url} alt={photo.name} className={`h-6 w-4 rounded-sm object-cover ${photo.ok === false ? "opacity-50" : ""}`} />
                    : <span className="inline-block h-6 w-4 rounded-sm bg-muted" />}
                  {photo.ok != null && (
                    <span className={`absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full text-[8px] font-bold ${photo.ok ? "bg-emerald-500 text-black" : "bg-red-500 text-white"}`}>
                      {photo.ok ? "✓" : "✕"}
                    </span>
                  )}
                </span>
              ))}
              {live.photos!.length > 5 && <span className="pl-1 text-[10px] text-muted-foreground">+{live.photos!.length - 5}</span>}
            </span>
          )}
          {live.beats && (
            <span
              className="inline-flex max-w-[280px] items-center gap-1.5 truncate rounded-full border border-border bg-black/30 px-2.5 py-1 text-[11px] text-muted-foreground" title={live.beats}
            >
              <ScrollText className="h-3 w-3 shrink-0 text-primary" />
              <span className="truncate">{live.beats}</span>
            </span>
          )}
          {live.renderCostCredits != null && live.renderCostCredits > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold text-amber-400">
               {t("studio.timeline.creditsToRender", { count: live.renderCostCredits.toLocaleString() })}
            </span>
          )}
        </div>
      )}

      <div
        ref={trackRef}
        className="studio-track relative overflow-x-auto pb-2" onMouseMove={handleScrub}
        onMouseLeave={() => setScrubSec(null)}
      >
        <Reorder.Group axis="x" values={clips} onReorder={handleReorder} as="div" className="flex items-center gap-1.5">
          {clips.map((clip, position) => (
            <ClipCard
              key={clip.id}
              clip={clip}
              position={position}
              zoom={zoom}
              isSample={isSample}
              trimEnabled={trimEnabled}
              selected={selectedIdx === clip.idx || scrubClipIdx === clip.idx}
              onSelect={handleSelect}
              onDuration={handleDuration}
              onApprove={handleApprove}
              onZoom={(idx) => setViewerIdx(clips.findIndex((c) => c.idx === idx))}
            />
          ))}
        </Reorder.Group>
        {scrubSec !== null && (
          <div className="pointer-events-none absolute inset-y-0" style={{ left: `${(scrubSec / Math.max(0.01, totalSec)) * 100}%` }}>
            <div className="h-full w-px bg-primary shadow-[0_0_8px_hsl(var(--primary))]" />
            <span className="absolute -top-0.5 left-1.5 rounded bg-primary px-1 py-0.5 text-[9px] font-bold text-primary-foreground">
              {formatSec(scrubSec)}
            </span>
          </div>
        )}
      </div>
      <TrackScroller trackRef={trackRef} contentKey={`${zoom}:${clips.length}`} />
      {viewerIdx !== null && viewerIdx >= 0 && (
        <MediaZoomViewer
          items={clips.map((clip, i) => ({ src: clip.imageUrl, kind: "image", label: clipTheme(t, clip) || t("studio.timeline.clipN", { n: i + 1 }) }))}
          index={Math.min(viewerIdx, clips.length - 1)}
          onIndex={setViewerIdx}
          onClose={() => setViewerIdx(null)}
        />
      )}

      {/* SELECTED-CLIP INSPECTOR — regenerate / steer / reorder for the clip you
          picked. Lives on the board (not the chat) so every edit is where the
          storyboard is. Only for a live run in the stills phase. */}
      {isLiveBoard && selectedClip && (
        <div className="mt-3 rounded-xl border border-border bg-black/30 p-3" data-testid="studio-clip-inspector">
          <div className="mb-2 flex items-center gap-2 text-xs">
            <span className="font-bold text-foreground">{t("studio.timeline.clipN", { n: selectedPos + 1 })}</span>
            <span className="text-muted-foreground">· {selectedClip.kind === "broll" ? t("studio.timeline.brollScene") : selectedClip.kind === "anchor" ? t("studio.timeline.anchor") : selectedClip.delivery === "scene" ? t("studio.timeline.artistInScene") : selectedClip.delivery === "crew" ? t("studio.timeline.artistWithCompany") : t("studio.timeline.artistOnCamera")}</span>
            {selectedClip.theme && <span className="truncate text-muted-foreground">· {clipTheme(t, selectedClip)}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => requestClipApprove(selectedClip.idx)}
              data-testid="studio-inspector-approve"
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold ${selectedClip.status === "approved" ? "border-emerald-500 bg-emerald-500/15 text-emerald-400" : "border-border text-foreground hover:bg-white/5"}`}
            >
              <Check className="h-3.5 w-3.5" />
              {selectedClip.status === "approved" ? t("studio.timeline.approved") : t("studio.timeline.approveClip")}
            </button>
            <button
              type="button"
              onClick={() => { requestClipRegen(selectedClip.idx, steer.trim() || undefined, reasons); setSteer(""); clearReasons(); }}
              disabled={anyRegenerating}
              data-testid="studio-inspector-regen"
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary bg-primary/10 px-3 py-1.5 text-xs font-bold text-foreground hover:bg-primary/20 disabled:opacity-40"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${selectedClip.status === "regenerating" ? "animate-spin" : ""}`} />
              {selectedClip.status === "regenerating" ? t("studio.timeline.regenerating") : t("studio.timeline.regenerate")}
            </button>
            <button
              type="button"
              onClick={() => moveSelected(-1)}
              disabled={selectedPos <= 0}
              className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs text-foreground hover:bg-white/5 disabled:opacity-30"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> {t("studio.timeline.earlier")}
            </button>
            <button
              type="button"
              onClick={() => moveSelected(1)}
              disabled={selectedPos < 0 || selectedPos >= clips.length - 1}
              className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs text-foreground hover:bg-white/5 disabled:opacity-30"
            >
              {t("studio.timeline.later")} <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {/* Optional "what's wrong" chips: sent with either redraw button. */}
          <div className="mt-2">
            <ReasonChips kind="stills" value={reasons} onChange={setReasons} disabled={anyRegenerating} testId="studio-inspector-reasons" />
          </div>
          {/* Steer this shot toward a direction, then regenerate around it. */}
          <div className="mt-2 flex items-center gap-2">
            <input
              value={steer}
              onChange={(event) => setSteer(event.target.value)}
              placeholder={t("studio.timeline.steerPlaceholder")}
              data-testid="studio-inspector-steer"
              className="min-w-0 flex-1 rounded-lg border border-border bg-black/40 px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground"
            />
            <button
              type="button"
              onClick={() => { requestClipRegen(selectedClip.idx, steer.trim() || undefined, reasons); setSteer(""); clearReasons(); }}
              disabled={anyRegenerating || !steer.trim()}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-primary bg-primary/10 px-3 py-1.5 text-xs font-bold text-foreground hover:bg-primary/20 disabled:opacity-40"
            >
              <Wand2 className="h-3.5 w-3.5" /> {t("studio.timeline.redoWithDirection")}
            </button>
          </div>
          <div className="mt-1.5 text-[10px] leading-relaxed text-muted-foreground">
            {t("studio.timeline.regenHintLead")} <b className="text-foreground">{t("studio.timeline.regenHintBold")}</b> {t("studio.timeline.regenHintTail")}
          </div>
        </div>
      )}

      {/* APPROVE-ALL + RENDER — the render gate, on the board. Disabled until every
          clip is approved. The price question, and any refusal, show right
          under the button (RenderGate): a click here always answers here. */}
      {isLiveBoard && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {!allApproved && (
            <button
              type="button"
              onClick={approveAll}
              data-testid="studio-approve-all"
              className="rounded-lg border border-emerald-500 bg-emerald-500/10 px-4 py-2 text-xs font-bold tracking-wide text-emerald-400 hover:bg-emerald-500/20"
            >
              {t("studio.timeline.approveAll", { count: clips.length })}
            </button>
          )}
          <button
            type="button"
            onClick={boardRender.render}
            disabled={!allApproved || anyRegenerating}
            data-testid="studio-render"
            title={allApproved ? undefined : t("studio.timeline.approveAllToUnlock")}
            className={`rounded-lg px-5 py-2 text-xs font-bold tracking-wide ${allApproved && !anyRegenerating ? "bg-gradient-to-r from-primary to-sky-500 text-white" : "cursor-default bg-white/10 text-muted-foreground"}`}
          >
            {allApproved
              ? board.renderFree ? t("studio.timeline.renderContinueFree")
              : (renderCost && renderCost > 0 ? t("studio.timeline.renderWithCost", { cost: renderCost.toLocaleString() }) : t("studio.timeline.renderNoCost"))
              : t("studio.timeline.awaitingApproval", { count: clips.filter((c) => c.status !== "approved").length })}
          </button>
          {renderCost != null && creditBalance != null && (
            <span className="text-[11px] text-muted-foreground">{t("studio.timeline.balance", { balance: creditBalance.toLocaleString() })}</span>
          )}
        </div>
      )}
      {/* On a phone the full-screen review sheet shows the same question; this
          copy under it must not take the keyboard. */}
      {isLiveBoard && <RenderGate board={board} unheard={boardRender.unheard} takeFocus={!narrow} />}

      <div className="mt-2 text-[10px] leading-relaxed text-muted-foreground">{t("studio.timeline.footerNote")}
      </div>
    </motion.section>
  );
}
